import { Helmet } from "react-helmet-async";

import Site from "@/config/site";

interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile";
  twitterHandle?: string;
  structuredData?: object;
}

const Seo = ({
  title,
  description,
  keywords,
  author = "Upscale BD",
  canonical,
  ogTitle,
  ogDescription,
  ogImage = "/assets/logo.svg",
  ogType = "website",
  twitterHandle = "@upscalebd",
  structuredData,
}: SeoProps) => {
  const fullTitle = title ? `${title} | ${Site.APP_NAME}` : Site.APP_NAME;
  const metaDescription =
    description ||
    "Upscale BD is a leading software company providing innovative digital solutions, web and mobile app development, and custom software services.";
  const url = window.location.href;

  return (
    <Helmet>
      {/* Standard Metadata */}
      <title>{fullTitle}</title>
      <meta content={metaDescription} name="description" />
      {keywords && <meta content={keywords} name="keywords" />}
      <meta content={author} name="author" />
      {canonical && <link href={canonical} rel="canonical" />}

      {/* Open Graph / Facebook */}
      <meta content={ogType} property="og:type" />
      <meta content={ogTitle || fullTitle} property="og:title" />
      <meta
        content={ogDescription || metaDescription}
        property="og:description"
      />
      <meta content={ogImage} property="og:image" />
      <meta content={url} property="og:url" />
      <meta content={Site.APP_NAME} property="og:site_name" />

      {/* Twitter */}
      <meta content="summary_large_image" name="twitter:card" />
      <meta content={ogTitle || fullTitle} name="twitter:title" />
      <meta
        content={ogDescription || metaDescription}
        name="twitter:description"
      />
      <meta content={ogImage} name="twitter:image" />
      {twitterHandle && <meta content={twitterHandle} name="twitter:site" />}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Mobile App Meta (Optional but good for SEO) */}
      <meta content="yes" name="apple-mobile-web-app-capable" />
      <meta
        content="black-translucent"
        name="apple-mobile-web-app-status-bar-style"
      />
    </Helmet>
  );
};

export default Seo;
